<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getImageBlob } from '../services/catalogService'

const props = defineProps<{
  imageId: string
  alt: string
}>()

const root = ref<HTMLElement | null>(null)
const src = ref('')
const loading = ref(false)
let observer: IntersectionObserver | null = null
let requestVersion = 0

function clearUrl(): void {
  if (src.value) URL.revokeObjectURL(src.value)
  src.value = ''
}

async function load(): Promise<void> {
  if (src.value || loading.value) return
  const version = ++requestVersion
  loading.value = true
  try {
    const blob = await getImageBlob(props.imageId, 'thumbnail')
    if (!blob || version !== requestVersion) return
    src.value = URL.createObjectURL(blob)
  } finally {
    if (version === requestVersion) loading.value = false
  }
}

function observe(): void {
  observer?.disconnect()
  if (!root.value) return
  observer = new IntersectionObserver((entries) => {
    const entry = entries[0]
    if (entry?.isIntersecting) void load()
    else if (src.value && entry && entry.boundingClientRect.top < -1200) clearUrl()
  }, { rootMargin: '600px 0px' })
  observer.observe(root.value)
}

watch(() => props.imageId, async () => {
  requestVersion += 1
  clearUrl()
  await nextTick()
  observe()
})

onMounted(observe)
onBeforeUnmount(() => {
  requestVersion += 1
  observer?.disconnect()
  clearUrl()
})
</script>

<template>
  <div ref="root" class="thumbnail-shell" :class="{ 'thumbnail-shell--loading': loading }">
    <img v-if="src" :src="src" :alt="alt" loading="lazy" decoding="async" />
    <div v-else class="thumbnail-placeholder" aria-hidden="true">
      <span class="thumbnail-shimmer" />
    </div>
  </div>
</template>
